import { UpperCasePipe } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { ListingService } from 'src/app/_shared/service/listings.service';
import { AppState } from 'src/app/app.state';
import { ProfileService } from 'src/app/profile/profile.service';
import constants from 'src/constant.json';
import { AgentLoSignupService } from '../../agent-lo-signup.service';

@Component({
  selector: 'app-agent-lo-signup',
  templateUrl: './agent-lo-signup.component.html',
  styleUrls: ['./agent-lo-signup.component.scss'],
})
export class AgentLoSignupComponent implements OnInit {
  @ViewChild('scrollTarget') scrollTarget!: ElementRef;

  upgradeSnackbar: MatSnackBarRef<any>;

  signupForm: FormGroup;
  mlsInfoForm: FormGroup;
  businessArea: any;
  userAddress;
  autoCompleteInput: any;
  propertySelected: any;
  showCreateAccount: boolean = false;
  initialMlsInfoFormValues: {};
  initialSignupFormValues: {};
  stateList = constants.STATES;
  isLoadingStateMLS = false;
  isLoadingdomains = false;
  stateMlsList = [];
  suggestedDomains: string[] = [];
  filteredDomains: string[] = [];
  mlsInformation = new MatTableDataSource<any>([]);
  isLoadingAgentMLS: boolean;
  mlsTableColumnList: string[] = ['state', 'mlsBoard', 'memberId', 'status', 'action'];
  showMlsInvalidMessage: boolean = false;
  isUserAuthenticationProgress: boolean = false;
  isSignUpVisibleSub$ = new BehaviorSubject<boolean>(false);
  isSignUpVisible$ = this.isSignUpVisibleSub$.asObservable();
  onDestroyNotifier$ = new Subject();
  autoComplete: google.maps.places.Autocomplete;
  mlsVerificationInProgress = false;
  isAccountCreated = false;
  redirectUrl: any;
  isSignUpSuccess: boolean = false;
  hidePassword: boolean = true;
  showMlsValidation = false;

  constructor(
    private formBuilder: FormBuilder,
    private agentLoSignupService: AgentLoSignupService,
    private snackBar: MatSnackBar,
    private profileService: ProfileService,
    private upperCasePipe: UpperCasePipe,
    public router: Router,
    public appState: AppState,
    private cdr: ChangeDetectorRef,
    private listingService: ListingService,
  ) { }

  ngOnInit() {
    this.mlsInfoForm = this.formBuilder.group({
      state: [''],
      mlsBoard: [''],
      memberId: [''],
    });
    this.initialMlsInfoFormValues = this.mlsInfoForm.value;

    this.signupForm = this.formBuilder.group(
      {
        firstName: [
          '',
          Validators.compose([Validators.maxLength(25), Validators.pattern('[a-zA-Z ]*'), Validators.required]),
        ],
        lastName: [
          '',
          Validators.compose([Validators.maxLength(25), Validators.pattern('[a-zA-Z ]*'), Validators.required]),
        ],
        email: ['', [Validators.required, Validators.pattern(constants.regex.validEmail)]],
        phone: [
          '',
          [
            Validators.required,
            Validators.pattern('^\\d{10}$'), // Just 10 digits
          ],
        ],
        companyName: [
          '',
          Validators.compose([Validators.maxLength(25), Validators.pattern('^[A-Z][a-zA-Z ]*$'), Validators.required]),
        ],
        subdomain: ['', Validators.required],
        role: ['', Validators.required],
        // password: [
        //   '',
        //   [
        //     Validators.required,
        //     Validators.minLength(8),
        //     Validators.maxLength(20),
        //     Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).+$/)
        //   ]
        // ],
        password: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            Validators.maxLength(20),
            this.passwordStrengthValidator()
          ]
        ],
        confirmPassword: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(20)]],
        businessArea: ['', Validators.required],
        mlsInfoForm: this.mlsInfoForm,
      },
      { validators: this.passwordMatchValidator('password', 'confirmPassword') }
    );

    this.signupForm.get('companyName')?.valueChanges.subscribe(() => {
      this.signupForm.get('subdomain')?.reset();
    });

    this.signupForm
      .get('companyName')
      ?.valueChanges.pipe(debounceTime(3000), distinctUntilChanged())
      .subscribe((value: string) => {
        if (value) {
          let params = {
            companyName: value,
          };
          this.fetchSuggestedDomains(params);
        }
      });

    this.signupForm
      .get('subdomain')
      ?.valueChanges.pipe(debounceTime(200), distinctUntilChanged())
      .subscribe((value) => {
        if (this.suggestedDomains && this.suggestedDomains.length > 0) {
          this.filteredDomains = this.suggestedDomains.filter((domain) =>
            domain.toLowerCase().includes((value || '').toLowerCase())
          );
        }
      });
  }

  onRoleChange(role: string) {
    this.signupForm.get('role')?.setValue(role);

    // Wait for the DOM to update after *ngIf renders the new section
    setTimeout(() => {
      this.scrollToBottom();
    }, 100);
  }

  scrollToBottom() {
    if (this.scrollTarget) {
      this.scrollTarget.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  fieldConfig = {
    phone: {
      mask: '(000) 000-0000',
    },
  };

  fetchSuggestedDomains(companyName): void {
    this.isLoadingdomains = true;
    this.agentLoSignupService
      .getSuggestedDomains(companyName)
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe(
        ({ isLoading, response }) => {
          setTimeout(() => {
            this.isLoadingdomains = false; // Hide the spinner after a small delay
          }, 2000);
          if (response && Array.isArray(response)) {
            this.suggestedDomains = response;
            this.filteredDomains = [...this.suggestedDomains]; // Or apply filter if needed
          }
        },
        (error) => {
          this.isLoadingdomains = false; // Hide the spinner in case of an error
          console.error('Error fetching suggested domains:', error);
        }
      );
  }


  passwordStrengthValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value || '';

      if (!value) return null;

      const errors: ValidationErrors = {};

      if (!/[A-Z]/.test(value)) errors.uppercase = true;
      if (!/[a-z]/.test(value)) errors.lowercase = true;
      if (!/\d/.test(value)) errors.number = true;
      if (!/[^A-Za-z\d]/.test(value)) errors.specialCharacter = true;

      return Object.keys(errors).length ? errors : null;
    };
  }

  passwordMatchValidator(controlName: string, matchingControlName: string): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const passwordControl = formGroup.get(controlName);
      const confirmPasswordControl = formGroup.get(matchingControlName);

      if (!passwordControl || !confirmPasswordControl) {
        return null;
      }

      if (passwordControl.value !== confirmPasswordControl.value) {
        confirmPasswordControl.setErrors({ passwordMismatch: true });
      } else {
        confirmPasswordControl.setErrors(null);
      }
      return null;
    };
  }

  onConfirmPasswordInput() {
    this.signupForm.get('confirmPassword').markAsDirty();
    this.signupForm.updateValueAndValidity();
  }

  getAddressComponents(property) {
    this.businessArea = property.fullAddress;
    this.propertySelected = property;
    if (
      !this.propertySelected.StreetAddress &&
      !this.propertySelected.City &&
      !this.propertySelected.State &&
      !this.propertySelected.PostalCode
    ) {
      this.openSnackBar('Search for a valid property.', 'snackbar-error');
    }
  }

  onDomainSelected(selectedValue: string): void {
    const suffix = '.prospektr.ai';

    const cleanValue = selectedValue.endsWith(suffix) ? selectedValue.slice(0, -suffix.length) : selectedValue;

    this.signupForm.get('subdomain')?.setValue(cleanValue);
  }
  agentLoSignup() {
    if (!this.propertySelected) {
      this.signupForm.controls.businessArea.setErrors({ message: 'Business Area is required' });
      return;
    }

    this.isUserAuthenticationProgress = true;
    this.cdr.detectChanges();

    const reqBody = {
      firstName: this.signupForm.value.firstName,
      lastName: this.signupForm.value.lastName,
      email: this.signupForm.value.email,
      password: this.signupForm.value.password,
      phone: {
        displayText: this.formatPhone(this.signupForm.value.phone),
        value: this.signupForm.value.phone,
      },
      role: this.signupForm.value.role,
      companyName: this.signupForm.value.companyName,
      subdomain: this.signupForm.value.subdomain,
      sendMail: true,
      mlsList: this.mlsInformation.data,
      businessArea: {
        streetAddress: this.propertySelected.StreetAddress,
        city: this.propertySelected.City,
        state: this.propertySelected.State,
        zipCode: this.propertySelected.PostalCode,
        coordinates: {
          lat: this.propertySelected.center.lat,
          long: this.propertySelected.center.lng,
        },
      },
    };

    this.agentLoSignupService
      .signUpForAgentLoanOfficer(reqBody)
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe({
        next: (response: any) => {
          if (response?.response) {
            if (response.response.message === 'MLS verification failed') {
              this.mlsVerificationInProgress = true;
            }

            if (response.response.data?.mlsList?.some(item => item.status === "verified")) {
              let params = {
                source: 'via get started',
                userId: response?.response?.data?.id,
                companyId: response?.response?.data?.companyId,
                mlsInfo: response.response.data?.mlsList.reduce((acc, item) => {
                  if (item.status === 'verified') {
                    acc[item.mlsBoard] = item.memberId;
                  }
                  return acc;
                }, {})
              }
              this.listingService
                .updateExpiredListings(params)
                .pipe(takeUntil(this.onDestroyNotifier$))
                .subscribe((res: any) => {
                });

            }

            if (response.response.data?.redirectUrl) {
              this.isSignUpSuccess = true;
              this.redirectUrl = response.response.data?.redirectUrl;
              // ✅ Reset both forms
              this.signupForm.reset();
              this.mlsInfoForm.reset();
              this.mlsInformation.data = [];

              Object.values(this.signupForm.controls).forEach((c) => c.setErrors(null));
              Object.values(this.mlsInfoForm.controls).forEach((c) => c.setErrors(null));

              // ✅ Open new tab
              // window.open(response.response.data.redirectUrl, '_blank');
            }
          } else {
            console.log('Unexpected empty signup response', response);
          }
        },
        error: (error) => {
          console.log('Signup failed', error);
        },
        complete: () => {
          this.isUserAuthenticationProgress = false;
          this.cdr.detectChanges();
        },
      });
  }

  formatPhone(phone: string): string {
    if (!phone) return '';
  
    const digits = phone.replace(/\D/g, '');
  
    if (digits.length === 10) {
      return `${digits.substring(0, 3)}-${digits.substring(3, 6)}-${digits.substring(6)}`;
    }
  
    return phone;
  }

  goBack() {
    this.isSignUpSuccess = false;
  }

  getStart() {
    // setTimeout(() => {
    window.open(this.redirectUrl, '_self');
    // }, 3000);
  }

  get signupControls() {
    return this.signupForm.controls;
  }

  get f() {
    return this.signupForm.controls;
  }

  private openSnackBar(message, style) {
    if (message) {
      this.snackBar.open(message, '', {
        duration: 5000,
        panelClass: [style ? style : ''],
        verticalPosition: 'top',
      });
    }
  }

  isMlsExists() {
    return this.mlsInformation.data.some(
      (val) =>
        val.state == this.mlsInfoForm.value.state &&
        val.mlsBoard == this.mlsInfoForm.value.mlsBoard &&
        val.memberId == this.mlsInfoForm.value.memberId
    );
  }

  addMlsInfo() {
      this.showMlsValidation = true;

  const state = this.mlsInfoForm.get('state');
  const mlsBoard = this.mlsInfoForm.get('mlsBoard');
  const memberId = this.mlsInfoForm.get('memberId');

  if (!state?.value || !mlsBoard?.value || !memberId?.value) {
    return;
  }

    // if (
    //   !this.mlsInfoForm.value.mlsBoard ||
    //   this.mlsInfoForm.value.mlsBoard == '' ||
    //   !this.mlsInfoForm.value.memberId ||s
    //   this.mlsInfoForm.value.memberId == '' ||
    //   !this.mlsInfoForm.value.state ||
    //   this.mlsInfoForm.value.state == ''
    // ) {
    //   return false;
    // }
    
    else if (this.isMlsExists()) {
      this.openSnackbar('This MLS is already Added', false);
      return false;
    } else {
      this.isLoadingAgentMLS = true;
      this.profileService
        .verifyMls(this.mlsInfoForm.value)
        .pipe(takeUntil(this.onDestroyNotifier$))
        .subscribe((res: any) => {
          this.mlsInformation = new MatTableDataSource(this.mlsInformation.data.concat(...res.data));
          this.stateMlsList = [];
          this.mlsInfoForm.patchValue({ mlsBoard : ''})
          this.mlsInfoForm.reset();
           this.showMlsValidation = false;
          this.isLoadingAgentMLS = false;
          this.validateMls();
          setTimeout(() => {
            this.scrollToBottom();
          }, 100);
        });
    }
  }

  onStateSelection(event) {
    this.isLoadingStateMLS = true;
    this.profileService
      .getMLSBoards(this.upperCasePipe.transform(event.value), true)
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe(({ isLoading, mlsBoard }: any) => {
        this.stateMlsList = [];
        this.isLoadingStateMLS = isLoading;
        if (mlsBoard && mlsBoard.length) {
          this.stateMlsList = mlsBoard;
        }
      });
  }

  deleteMLS(mlsId) {
    this.mlsInformation.data.splice(this.mlsInformation.data.indexOf(mlsId), 1);
    this.mlsInformation = new MatTableDataSource(this.mlsInformation.data);
    this.validateMls();
  }

  openSnackbar(upgradeMsg, success = false) {
    this.upgradeSnackbar = this.snackBar.open(upgradeMsg, 'Dismiss', {
      duration: 5000,
      panelClass: [success ? 'snackbar-success' : 'snackbar-error'],
      verticalPosition: 'top',
    });
  }

  goToSignIn() {
    const baseUrl = this.appState.companyInfoValue.baseUrl;
    const loginUrl = `https://${baseUrl}/corporate-login/email`;
    window.location.href = loginUrl;
  }

  validateMls() {
    this.okFuncSignup();
    if (this.mlsInformation.data.filter((e) => e.status === 'verified').length > 0) {
      this.showCreateAccount = true;
    } else {
      this.showCreateAccount = false;
    }
  }

  okFuncSignup() {
    if (this.mlsInformation.data.filter((e) => e.status === 'invalid').length > 0) {
      this.showMlsInvalidMessage = true;
    } else {
      this.showMlsInvalidMessage = false;
    }
  }
  resetAllForms() {
    this.mlsInfoForm.reset(this.initialMlsInfoFormValues);
    this.signupForm.reset(this.initialSignupFormValues);
  }

  backToHome() {
    this.mlsVerificationInProgress = false;
    window.location.href = 'https://www.prospektr.ai';
  }
}
